/**
 * Unlock — paywall page for the $2.99 full game unlock.
 *
 * Shows one of three states based on the pilot's entitlement snapshot:
 *   - "needs_trial" — big "Start 7-day trial" CTA + secondary "Buy now"
 *   - "trial"       — "X days left" progress bar + "Buy now to secure"
 *   - "expired"     — trial-ended message + big "Buy now" CTA
 *   - "paid"/"grandfathered" — auto-redirect to /levels (they shouldn't be here)
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Lock, Rocket, Check } from "lucide-react";
import Navbar from "../components/Navbar";
import api from "../lib/api";
import useGameAccess from "../lib/useGameAccess";

export default function Unlock() {
  const navigate = useNavigate();
  const { snapshot, loading, refresh } = useGameAccess();
  const [busy, setBusy] = useState(false);
  const [founder, setFounder] = useState(null);

  useEffect(() => {
    if (snapshot && ["paid", "grandfathered"].includes(snapshot.access)) {
      navigate("/levels", { replace: true });
    }
  }, [snapshot, navigate]);

  useEffect(() => {
    api.get("/entitlements/founders-status")
      .then((r) => setFounder(r.data))
      .catch(() => {});
  }, []);

  const startTrial = async () => {
    setBusy(true);
    try {
      await api.post("/entitlements/start-trial");
      toast.success("Trial started — 7 days of full access, no card needed.");
      await refresh();
      navigate("/levels");
    } catch (e) {
      toast.error("Couldn't start trial", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const buyNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/entitlements/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Checkout failed", { description: e?.response?.data?.detail || e.message });
      setBusy(false);
    }
  };

  const price = snapshot?.price_usd ?? 2.99;
  const access = snapshot?.access;
  const daysLeft = snapshot?.days_left;

  return (
    <div className="min-h-screen bg-[#05050a] text-white">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#00ffff] mb-2">
          // UNLOCK · FULL GAME
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-4">
          {access === "expired" ? "Trial Ended" : access === "trial" ? "Trial Active" : "Cosmic Worm Breaker"}
        </h1>

        {loading ? (
          <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/50">
            Loading…
          </div>
        ) : (
          <>
            {/* Founder's Edition scarcity banner — only surfaces while
                slots remain. Uses gold-on-obsidian to feel premium. */}
            {founder && founder.remaining > 0 && (
              <div
                className="mb-4 border border-[#ffd700]/60 bg-gradient-to-r from-[#ffd700]/[0.10] via-[#b026ff]/[0.08] to-[#ffd700]/[0.10] px-4 py-3"
                data-testid="founder-scarcity-banner"
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#ffd700]" />
                    <div>
                      <div className="font-heading uppercase tracking-tight text-sm text-[#ffd700]">
                        Founder&apos;s Edition · {founder.remaining} slots left
                      </div>
                      <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/60 mt-0.5">
                        Exclusive skin awarded to the first {founder.cap} buyers · #{founder.claimed + 1} available
                      </div>
                    </div>
                  </div>
                  <div className="font-heading text-2xl tabular-nums text-[#ffd700]">
                    {founder.claimed}<span className="text-white/30">/{founder.cap}</span>
                  </div>
                </div>
                <div className="mt-2 h-1 bg-white/10 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#ffd700] to-[#b026ff] transition-all"
                    style={{ width: `${(founder.claimed / founder.cap) * 100}%` }}
                    data-testid="founder-progress-bar"
                  />
                </div>
              </div>
            )}

            {/* Trial-active status */}
            {access === "trial" && (
              <div
                className="mb-6 border border-[#00ffff]/40 bg-[#00ffff]/[0.05] p-4"
                data-testid="unlock-trial-status"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Rocket className="w-4 h-4 text-[#00ffff]" />
                  <div className="font-heading uppercase tracking-tight text-base">
                    {daysLeft} day{daysLeft === 1 ? "" : "s"} left · full trial access
                  </div>
                </div>
                <div className="text-sm text-white/70">
                  Buy the full game any time to lock in permanent access + 3 free starter skins.
                </div>
              </div>
            )}

            {access === "expired" && (
              <div className="mb-6 border border-[#ff007f]/50 bg-[#ff007f]/[0.06] p-4" data-testid="unlock-expired-status">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-[#ff007f]" />
                  <div className="font-heading uppercase tracking-tight text-base">
                    Your 7-day trial has ended
                  </div>
                </div>
                <div className="text-sm text-white/70">
                  Your progress, skins, and stats are safe. Grab the full game to keep playing.
                </div>
              </div>
            )}

            {/* Pricing card */}
            <div
              className="border border-[#ffd700]/50 bg-gradient-to-br from-[#ffd700]/[0.08] to-transparent p-6 mb-6"
              data-testid="unlock-pricing-card"
            >
              <div className="flex items-baseline gap-2 mb-3">
                <span className="font-heading text-5xl text-[#ffd700] tabular-nums">${price}</span>
                <span className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/50">
                  · one-time · lifetime access
                </span>
              </div>
              <ul className="space-y-2 mb-5 text-sm">
                {[
                  "Full campaign — 3000 procedural levels across 6 sectors",
                  "Endless mode + Daily seeded runs + Wormhole hunters",
                  "Boss battles: Leviathans, rare worms, Space Anglerfish",
                  "3 free starter ball skins (you pick after purchase)",
                  "Cloud saves + achievements + friend leaderboards",
                  "No ads · no subscription · no expiring content",
                  ...(founder && founder.remaining > 0
                    ? [`🏆 Founder's Edition skin — first ${founder.cap} buyers only (${founder.remaining} left)`]
                    : []),
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2 text-white/85">
                    <Check className="w-4 h-4 text-[#39ff14] flex-shrink-0 mt-0.5" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={buyNow}
                disabled={busy}
                className="w-full sm:w-auto px-5 py-3 bg-[#ffd700] hover:bg-[#ffed7a] text-black font-heading uppercase tracking-tight text-base transition disabled:opacity-40"
                data-testid="unlock-buy-now-btn"
              >
                {busy ? "Opening checkout…" : `Buy Full Game · $${price}`}
              </button>
            </div>

            {/* Trial CTA — only when trial is available */}
            {access === "needs_trial" && (
              <div
                className="border border-[#00ffff]/40 bg-[#00ffff]/[0.04] p-6"
                data-testid="unlock-trial-cta"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-[#00ffff]" />
                  <div className="font-heading uppercase tracking-tight text-base">
                    Not sure yet? Try before you buy.
                  </div>
                </div>
                <div className="text-sm text-white/70 mb-4">
                  Get 7 days of full access — no card, no obligation, no auto-charge.
                  Grandfathered pilots get free forever.
                </div>
                <button
                  type="button"
                  onClick={startTrial}
                  disabled={busy}
                  className="px-5 py-3 border-2 border-[#00ffff] text-[#00ffff] hover:bg-[#00ffff]/10 font-heading uppercase tracking-tight text-base transition disabled:opacity-40"
                  data-testid="unlock-start-trial-btn"
                >
                  {busy ? "Starting…" : "Start 7-day Free Trial"}
                </button>
              </div>
            )}

            <div className="mt-8 font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/40">
              Secure checkout by Stripe · <Link to="/terms" className="hover:text-white/60">Terms</Link> · <Link to="/privacy" className="hover:text-white/60">Privacy</Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
